'use client';

import { Bridge3d, CalendarSolid, HomeSolid, MapPinSolid } from '@byway/icons';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { PlaygroundIndexForm } from '~/components/Playground/PlaygroundIndexForm/PlaygroundIndexForm';
import {
  PlaygroundStartEndTabs,
  TabbedPlaygroundIndexOriginsForm
} from '~/components/Playground/PlaygroundStartEndTabs/PlaygroundStartEndTabs';
import { TabsContainer } from '~/components/Tabs';
import { type CreateTripTab, usePlaygroundActions, usePlaygroundUIState } from '~/stores/playgroundStore';
import styles from './index.module.css';

const bg = 'https://www.datocms-assets.com/57243/1733247875-playground-img.jpg';

export default function PlaygroundIndexClient() {
  const t = useTranslations('content.map');
  const { setStartDate, setEndDate, setCreateTripTab } = usePlaygroundActions();
  const { createTripTab } = usePlaygroundUIState();
  const searchParams = useSearchParams();
  const csUser = searchParams?.get('csUser') ?? '';
  const areOneWayTripsEnabled = process.env.NEXT_PUBLIC_FF_ENABLE_ONE_WAY_TRIPS === 'true';

  useEffect(() => {
    // Store csUser in sessionStorage
    if (csUser) {
      sessionStorage.setItem('csUser', csUser);
    }

    const timeout = setTimeout(() => {
      setStartDate(null);
      setEndDate(null);
    }, 700);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.underlay} />
      {areOneWayTripsEnabled ? (
        <TabsContainer
          selectedKey={createTripTab}
          onSelectionChange={(key) => {
            setCreateTripTab(key as CreateTripTab);
          }}
        >
          <div className={styles.headerRow}>
            <PlaygroundStartEndTabs />
          </div>
          <TabbedPlaygroundIndexOriginsForm isHeroCta={false} variant="module" travelType={createTripTab} />
        </TabsContainer>
      ) : (
        // With no tab list, react-aria Tabs would throw on TabPanels and mis-render the form's own nested tabs.
        <PlaygroundIndexForm isHeroCta={false} variant="module" travelType={createTripTab} />
      )}

      {/* Image Container - Will be hidden on mobile via CSS */}
      <div className={styles.imageContainer}>
        <Image src={bg} alt={t('image_alt')} fill className={styles.coverImage} />
        <Timeline />
      </div>
    </div>
  );
}

function Timeline() {
  const t = useTranslations('content.map');

  return (
    <>
      <div className={styles.timelineUnderlay} />
      <div className={styles.timelineGroup}>
        <div className={styles.timeline}>
          <div className={styles.startCircles}>
            <div className={styles.timelineIconCircle}>
              <HomeSolid color="black" size="small" decorative />
            </div>
            <div className={styles.timelineIconCircle}>
              <CalendarSolid color="black" size="small" decorative />
            </div>
            <div className={styles.timelineIconCircle}>
              <MapPinSolid color="black" size="small" decorative />
            </div>
          </div>
          <div className={styles.endCircle}>
            <div className={styles.timelineIconCircle}>
              <Bridge3d color="black" size="small" decorative />
            </div>
          </div>
        </div>
        <div className={styles.timelineTextContainer}>
          <p className={styles.timelineText}>
            <span className={styles.timelineTextLong}>{t('timeline_start')}</span>
            <span className={styles.timelineTextShort}>{t('timeline_start_short')}</span>
          </p>
          <div className={styles.timelineEnd}>
            <p className={styles.timelineText}>{t('timeline_end')}</p>
          </div>
        </div>
      </div>
    </>
  );
}
